import "@mideye/design-tokens/tokens.css";
import { useLayoutEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createAegisTheme } from "@/theme";
import FeatureFlagProvider from "@/components/FeatureFlagProvider.tsx";
import { AppShell } from "@/components/Layout";
import SecurityOverviewPage from "@/pages/SecurityOverviewPage.tsx";
import CredentialHealthPage from "@/pages/CredentialHealthPage.tsx";
import EventsPage from "@/pages/EventsPage.tsx";
import AlertsPage from "@/pages/AlertsPage.tsx";
import VulnerabilityWatchPage from "@/pages/VulnerabilityWatchPage.tsx";
import VulnerabilityPage from "@/pages/VulnerabilityPage.tsx";
import { useFeatureFlag } from "@/hooks/useFeatureFlags.tsx";

function isHttpStatus(error: Error, status: number): boolean {
  return "status" in error && (error as { status: number }).status === status;
}

/**
 * Preserves query string and hash when redirecting the legacy /events path to
 * /ip-activity, so existing bookmarks and deep-links continue to work.
 */
export function RedirectEventsToIpActivity() {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: "/ip-activity", search: location.search, hash: location.hash }}
      replace
    />
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof Error && isHttpStatus(error, 401)) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function useDesignTokenTheme(mode: "light" | "dark") {
  useLayoutEffect(() => {
    const previousTheme = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = mode;

    return () => {
      if (previousTheme === undefined) {
        delete document.documentElement.dataset.theme;
        return;
      }
      document.documentElement.dataset.theme = previousTheme;
    };
  }, [mode]);
}

export default function App() {
  useDesignTokenTheme("light");
  const theme = useMemo(() => createAegisTheme("light"), []);

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppShell>
            <AppRoutes />
          </AppShell>
        </ThemeProvider>
      </FeatureFlagProvider>
    </QueryClientProvider>
  );
}

function AppRoutes() {
  const credentialHealthEnabled = useFeatureFlag("credentialHealth");
  const alertsEnabled = useFeatureFlag("alerts");
  const vulnWatchEnabled = useFeatureFlag("vulnWatch");
  return (
    <Routes>
      <Route path="/" element={<SecurityOverviewPage />} />
      {credentialHealthEnabled && (
        <Route path="/credential-health" element={<CredentialHealthPage />} />
      )}
      <Route path="/ip-activity" element={<EventsPage />} />
      {alertsEnabled && <Route path="/alerts" element={<AlertsPage />} />}
      {vulnWatchEnabled && <Route path="/vulnerability-watch" element={<VulnerabilityWatchPage />} />}
      {vulnWatchEnabled && <Route path="/vulnerability-watch/:cve" element={<VulnerabilityPage />} />}
      <Route path="/events" element={<RedirectEventsToIpActivity />} />
    </Routes>
  );
}
